import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

function formatDateTime(dt: string): string {
  return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface InquiryRow {
  id: string;
  auction_id: string;
  frage: string;
  kontakt_email: string;
  kontakt_name: string | null;
  kontakt_unternehmen: string | null;
  status: string;
  created_at: string;
  beantwortet_at: string | null;
  admin_notiz: string | null;
  auctions?: { aktenzeichen: string; titel: string };
}

export function AdminAnfragenPage() {
  const [inquiries, setInquiries] = useState<InquiryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<InquiryRow | null>(null);

  useEffect(() => { loadInquiries(); }, []);

  async function loadInquiries() {
    const { data } = await supabase
      .from('inquiries')
      .select('*, auctions(aktenzeichen, titel)')
      .order('created_at', { ascending: false });
    setInquiries((data || []) as InquiryRow[]);
    setLoading(false);
  }

  async function markAnswered(id: string) {
    await supabase
      .from('inquiries')
      .update({ status: 'BEANTWORTET', beantwortet_at: new Date().toISOString() })
      .eq('id', id);
    loadInquiries();
  }

  const filtered = statusFilter === 'all'
    ? inquiries
    : inquiries.filter((i) => i.status === statusFilter);

  if (loading) return <div className="container-content py-12"><p className="text-text-secondary">Laden...</p></div>;

  return (
    <div className="container-content py-12">
      <h1 className="text-2xl font-serif mb-6">Eingehende Anfragen</h1>

      <div className="flex gap-3 mb-6">
        {['all', 'OFFEN', 'BEANTWORTET'].map((f) => (
          <button key={f} onClick={() => setStatusFilter(f)}
            className={`px-3 py-1 text-sm rounded-sm border ${statusFilter === f ? 'bg-primary text-white border-primary' : 'border-surface-border text-text-secondary hover:bg-surface-alt'}`}>
            {f === 'all' ? 'Alle' : f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-text-secondary text-sm">Keine Anfragen vorhanden.</p>
      ) : (
        <table className="min-w-full">
          <thead>
            <tr><th>Auktion</th><th>Absender</th><th>Unternehmen</th><th>Frage</th><th>Eingegangen</th><th>Status</th><th>Aktion</th></tr>
          </thead>
          <tbody>
            {filtered.map((inq) => (
              <tr key={inq.id} onClick={() => setSelected(inq)} className="cursor-pointer hover:bg-surface-alt/50">
                <td className="font-mono text-sm">{inq.auctions?.aktenzeichen}</td>
                <td>{inq.kontakt_name}</td>
                <td>{inq.kontakt_unternehmen || '—'}</td>
                <td className="max-w-xs truncate">{inq.frage}</td>
                <td className="text-sm">{formatDateTime(inq.created_at)}</td>
                <td><span className="font-mono text-xs">{inq.status}</span></td>
                <td>
                  {inq.status === 'OFFEN' && (
                    <button onClick={(e) => { e.stopPropagation(); markAnswered(inq.id); }} className="btn-secondary text-xs">
                      Beantwortet
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-surface p-6 rounded-sm max-w-lg w-full mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-serif mb-4">
              Anfrage — {selected.auctions?.aktenzeichen}
            </h2>
            <div className="space-y-3 text-sm">
              <p><strong>Absender:</strong> {selected.kontakt_name || '—'}</p>
              <p><strong>Unternehmen:</strong> {selected.kontakt_unternehmen || '—'}</p>
              <p><strong>E-Mail:</strong> {selected.kontakt_email}</p>
              <p><strong>Frage:</strong></p>
              <div className="p-3 bg-surface-alt rounded-sm border border-surface-border">{selected.frage}</div>
              <p><strong>Zeit:</strong> {formatDateTime(selected.created_at)}</p>
              <p><strong>Status:</strong> {selected.status}</p>
            </div>
            <div className="flex gap-3 mt-6">
              {selected.status === 'OFFEN' && (
                <button onClick={() => { markAnswered(selected.id); setSelected(null); }} className="btn-primary">
                  Als beantwortet markieren
                </button>
              )}
              <button onClick={() => setSelected(null)} className="btn-secondary">Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
